import Image, { type StaticImageData } from "next/image";
import facebookIcon from "@rosette/assets/src/icons/facebook.svg";
import discordIcon from "@rosette/assets/src/icons/discord.svg";
import redditIcon from "@rosette/assets/src/icons/reddit.svg";
import instagramIcon from "@rosette/assets/src/icons/instagram.svg";
import twitterIcon from "@rosette/assets/src/icons/twitter.svg";
import Link from "next/link";
import {
    DISCORD_INVITE_URL,
    FACEBOOK_PAGE_URL,
    INSTAGRAM_PAGE_URL,
    KOFI_URL,
    REDDIT_URL, TWITTER_URL,
} from "@rosette/ts/src/business/constants.js";
import { RoyalBanner } from "@/components/social/banner/RoyalBanner.js";
import { FooterProfileLink } from "@/components/layout/FooterProfileLink.js";
import { CookiePreferencesButton } from "@rosette/web_components/src/cookie/CookiePreferencesButton.js";
import { cn } from "@rosette/ts/src/util/cn.js";


const COLUMN_TITLE_CLASSES = "font-[sans-serif] align-middle text-xl font-bold text-strong m-0";
const LINK_CLASSES = "inline-block text-left font-[sans-serif] align-middle text-xl text-muted";
const SMALL_PRINT_CLASSES = "font-[sans-serif] align-middle text-base text-subtle my-0";


interface SocialIconLinkProps {
    href: string;
    icon: StaticImageData;
    alt: string;
    height?: number;
}

function SocialIconLink({ href, icon, alt, height = 48 }: SocialIconLinkProps) {
    return (
        <Link className="inline-block leading-0 w-[2.6em]" href={href}>
            <Image
                src={icon}
                alt={alt}
                className="w-[2.6em]"
                width="48"
                height={height} />
        </Link>
    );
}


interface FooterProps {
    className?: string;
}

export default function Footer({ className }: FooterProps) {
    const buildVersion = process.env.NEXT_PUBLIC_BUILD_VERSION ?? "local";

    return (
        <footer
            className={cn(
                "inline-block box-border w-full mt-auto mx-0 mb-0 z-1 p-9 pb-36 md:pb-14",
                "bg-surface-sunken text-[1.5em] text-left",
                className ?? null,
            )}>
            <div className="flex flex-col md:flex-row flex-wrap items-start gap-14 w-full max-w-3xl mx-auto">

                <div className="flex flex-row items-start gap-x-14 grow">

                    <div className="flex flex-col gap-y-3 w-full max-w-52">
                        <div className="flex flex-col gap-y-3 mb-6">
                            <div className={COLUMN_TITLE_CLASSES}>
                                The Game
                            </div>
                            <Link className={LINK_CLASSES} href="/">
                                Home
                            </Link>
                            <Link className={LINK_CLASSES} href="/game">
                                Play
                            </Link>
                            <Link className={LINK_CLASSES} href="/rules">
                                Rules
                            </Link>
                            <FooterProfileLink className={LINK_CLASSES} />
                        </div>

                        <div className="flex flex-col gap-y-3 mb-6">
                            <div className={COLUMN_TITLE_CLASSES}>
                                Policies
                            </div>
                            <Link className={LINK_CLASSES} href="/privacy">
                                Privacy Policy
                            </Link>
                            <Link className={LINK_CLASSES} href="/terms">
                                Terms & Conditions
                            </Link>
                            <Link className={LINK_CLASSES} href="/cookies">
                                Cookie Policy
                            </Link>
                            <CookiePreferencesButton className={LINK_CLASSES} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-y-3 w-full max-w-52">
                        <div className="flex flex-col gap-y-3 mb-6">
                            <div className={COLUMN_TITLE_CLASSES}>
                                About Us
                            </div>
                            <Link className={LINK_CLASSES} href="/about">
                                About
                            </Link>
                            <Link className={LINK_CLASSES} href="/blog">
                                Blog
                            </Link>
                            <Link className={LINK_CLASSES} href={KOFI_URL}>
                                Ko-Fi
                            </Link>
                            <Link className={LINK_CLASSES} href="/contact">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center md:items-end grow-0 mx-auto">
                    <RoyalBanner className="max-w-[10em] m-0" />
                    <span className={SMALL_PRINT_CLASSES}>
                        © Rosette Games Ltd, 2025
                    </span>
                    <span className={SMALL_PRINT_CLASSES}>
                        {buildVersion}
                    </span>
                    <div className="flex flex-wrap flex-row-reverse items-center md:items-start justify-center md:justify-start gap-[1.2em] max-w-[12em] mt-4">
                        <SocialIconLink href={FACEBOOK_PAGE_URL} icon={facebookIcon} alt="Facebook" />
                        <SocialIconLink href={INSTAGRAM_PAGE_URL} icon={instagramIcon} alt="Instagram" />
                        <SocialIconLink href={DISCORD_INVITE_URL} icon={discordIcon} alt="Discord" />
                        <SocialIconLink href={REDDIT_URL} icon={redditIcon} alt="Reddit" />
                        <SocialIconLink href={TWITTER_URL} icon={twitterIcon} alt="Twitter" height={39} />
                    </div>
                </div>
            </div>
        </footer>
    );
}
